
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Save } from 'lucide-react';
import { SEAT_PREFERENCE_OPTIONS } from '@/utils/seatPreference';
import { AIRLINE_PROGRAMS } from '@/constants/loyaltyPrograms';
import DobInput from '@/components/passenger/DobInput';
import { COUNTRIES } from '@/data/countries';
import { countryName, normalizeCountry } from '@/utils/travelRequirements';

// The picker's value for a stored country: its name, or '' for legacy free
// text that isn't a country ("White"), so the placeholder shows.
// Reads a saved-traveler field by name (the traveler arrives as a plain row).
const fieldOf = (row, key) => (row ? row[key] : undefined);

const countryPickerValue = (value) => {
  const code = normalizeCountry(value);
  return code ? countryName(code) : '';
};

export default function SavedPassengerForm({ passenger, onSave, onClose, isSaving, error }) {
  const [formData, setFormData] = useState({
    first_name: '', middle_name: '', last_name: '', gender: '',
    date_of_birth: '', email: '', phone: '',
    passport_number: '', passport_expiry: '', nationality: '', passport_country: '',
    frequent_flyer_number: '', frequent_flyer_program: '', known_traveler_number: '',
    seat_preference: ''
  });

  useEffect(() => {
    if (passenger) {
      setFormData({
        first_name: passenger.first_name || '',
        middle_name: passenger.middle_name || '',
        last_name: passenger.last_name || '',
        gender: passenger.gender || '',
        date_of_birth: passenger.date_of_birth || '',
        email: passenger.email || '',
        phone: passenger.phone || '',
        passport_number: passenger.passport_number || '',
        passport_expiry: passenger.passport_expiry || '',
        nationality: countryPickerValue(passenger.nationality),
        passport_country: countryPickerValue(fieldOf(passenger, 'passport_country')),
        frequent_flyer_number: passenger.frequent_flyer_number || '',
        frequent_flyer_program: passenger.frequent_flyer_program || '',
        known_traveler_number: passenger.known_traveler_number || '',
        seat_preference: passenger.seat_preference || ''
      });
    } else {
      setFormData({
        first_name: '', middle_name: '', last_name: '', gender: '',
        date_of_birth: '', email: '', phone: '',
        passport_number: '', passport_expiry: '', nationality: '', passport_country: '',
        frequent_flyer_number: '', frequent_flyer_program: '', known_traveler_number: '',
        seat_preference: ''
      });
    }
  }, [passenger]);

  // Date of birth is optional here, so a half-typed one would otherwise save
  // the traveler with no date at all (DobInput reports '' until it's whole).
  const [dobIncomplete, setDobIncomplete] = useState(false);
  const [dobNudge, setDobNudge] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (dobIncomplete) {
      setDobNudge(true);
      return;
    }
    onSave(formData);
  };
  
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-24 overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative glass rounded-2xl p-6 max-w-2xl w-full my-8 overflow-x-hidden"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {passenger ? 'Edit Saved Passenger' : 'Add New Passenger'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/10 hover:text-white rounded-full">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 overflow-x-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Gender</Label>
              <Select value={formData.gender} onValueChange={v => handleChange('gender', v)}>
                <SelectTrigger className="glass text-white border-white/20"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent><SelectItem value="M">Male</SelectItem><SelectItem value="F">Female</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white">First Name *</Label>
              <Input value={formData.first_name} onChange={e => handleChange('first_name', e.target.value)} required className="glass text-white border-white/20"/>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Middle Name</Label>
              <Input value={formData.middle_name} onChange={e => handleChange('middle_name', e.target.value)} className="glass text-white border-white/20"/>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Last Name *</Label>
              <Input value={formData.last_name} onChange={e => handleChange('last_name', e.target.value)} required className="glass text-white border-white/20"/>
            </div>
            <div className="space-y-2">
              <Label htmlFor="saved-traveler-dob" className="text-white">Date of Birth</Label>
              {/* Typed, not the native picker: on iPhone the picker opens on
                  today's date and can clamp the day (CPDUIJ booked 6/30 for a
                  6/23 birthday), and saved travelers fill in checkout. */}
              <DobInput
                id="saved-traveler-dob"
                value={formData.date_of_birth}
                onChange={(iso) => { handleChange('date_of_birth', iso); setDobNudge(false); }}
                onIncompleteChange={setDobIncomplete}
                showHint={dobNudge}
                className="glass text-white border-white/20"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Email</Label>
              <Input type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} className="glass text-white border-white/20"/>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Phone</Label>
              <Input type="tel" value={formData.phone} onChange={e => handleChange('phone', e.target.value)} className="glass text-white border-white/20"/>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Passport Number</Label>
              <Input value={formData.passport_number} onChange={e => handleChange('passport_number', e.target.value)} className="glass text-white border-white/20"/>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Passport Expiry</Label>
              <Input type="date" value={formData.passport_expiry} onChange={e => handleChange('passport_expiry', e.target.value)} className="glass text-white border-white/20"/>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Nationality</Label>
              <Select value={formData.nationality} onValueChange={v => handleChange('nationality', v)}>
                <SelectTrigger className="glass text-white border-white/20" aria-label="Nationality"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {COUNTRIES.map(([code, name]) => (
                    <SelectItem key={code} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Passport Issued By</Label>
              <Select value={formData.passport_country} onValueChange={v => handleChange('passport_country', v)}>
                <SelectTrigger className="glass text-white border-white/20" aria-label="Passport issuing country"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {COUNTRIES.map(([code, name]) => (
                    <SelectItem key={code} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Frequent Flyer Program</Label>
              <Select value={formData.frequent_flyer_program} onValueChange={v => handleChange('frequent_flyer_program', v)}>
                <SelectTrigger className="glass text-white border-white/20"><SelectValue placeholder="Select program..." /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {AIRLINE_PROGRAMS.map(program => (
                    <SelectItem key={program.value} value={program.value}>{program.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Frequent Flyer #</Label>
              <Input value={formData.frequent_flyer_number} onChange={e => handleChange('frequent_flyer_number', e.target.value)} className="glass text-white border-white/20"/>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Known Traveler #</Label>
              <Input value={formData.known_traveler_number} onChange={e => handleChange('known_traveler_number', e.target.value)} className="glass text-white border-white/20"/>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Seat Preference</Label>
              <Select value={formData.seat_preference} onValueChange={v => handleChange('seat_preference', v)}>
                <SelectTrigger className="glass text-white border-white/20"><SelectValue placeholder="Select preference..." /></SelectTrigger>
                <SelectContent>
                  {SEAT_PREFERENCE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {error && (
            <div className="mt-4 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 text-sm">
              {error}
            </div>
          )}
          
          <div className="pt-4 flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving} className="glass border-white/20 text-white hover:bg-white/10 hover:text-white">
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving} className="bg-[var(--accent-color)] hover:bg-primary/90 text-white disabled:opacity-50">
              <Save className="w-4 h-4 mr-2" /> 
              {isSaving ? 'Saving...' : 'Save Passenger'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
